/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module CloudModule
 */

import { isUndefined } from 'underscore';
import {
    HttpMethod,
    HttpWrapper,
    HTTP_WRAPPER_TOKEN,
} from 'ajs/modules/core/factories/http-wrapper';
import { StringService } from 'string-service';
import { MessageItem } from 'ajs/modules/data-model/factories/message-item.factory';

import {
    INsxtConfiguration,
    INsxtSegment,
    INsxtTier1,
    INsxtTransportzone,
    INsxtVcenterIp,
    NsxtTransportZoneType,
} from 'generated-types';
import {
    DataNetworkConfigConfigItem,
    ManagementNetworkConfigConfigItem,
} from '.';

const NSXT_BASE_API = '/api/nsxt';
const TRANSPORT_ZONES_API = `${NSXT_BASE_API}/transportzones`;
const SEGMENTS_API = `${NSXT_BASE_API}/segments`;
const TIER1S_API = `${NSXT_BASE_API}/tier1s`;
const VCENTERS_API = `${NSXT_BASE_API}/vcenters`;
const VERIFY_LOGIN_API = `${NSXT_BASE_API}/verify/login`;

const VERIFY_LOGIN_REQUEST_ID = 'verify-login';

type TNsxtConfigurationPartial = Omit<INsxtConfiguration,
'management_segment' | 'tier1_segment_config' | 'data_network_config' | 'management_network_config'
>;

interface INsxtConfigurationConfig extends TNsxtConfigurationPartial {
    management_network_config?: ManagementNetworkConfigConfigItem;
    data_network_config?: DataNetworkConfigConfigItem;
}

/**
 * Interface of the payload used in the request to fetch Tier1LR segments. Segments can be fetched
 * with the credentials_uuid, host, and transport_zone_id, or in the case of VMC, just the
 * cloud_uuid.
 */
interface ISegmentsRequestPayload {
    credentials_uuid?: string;
    host?: string;
    transport_zone_id?: string;
    tier1_id?: string;
    cloud_uuid?: string;
}

export type TNsxtLoginCredentials =
    Pick<INsxtConfigurationConfig, 'nsxt_url' | 'nsxt_credentials_ref'>;

export class NsxtConfigurationConfigItem extends MessageItem<INsxtConfigurationConfig> {
    /**
     * HttpWrapper instance to make HTTP Requests.
     */
    private httpWrapper: HttpWrapper;

    /**
     * StringService instance to work with string.
     */
    private readonly stringService: StringService;

    constructor(args = {}) {
        const extendedArgs = {
            objectType: 'NsxtConfiguration',
            ...args,
        };

        super(extendedArgs);

        const HttpWrapper = this.getAjsDependency_(HTTP_WRAPPER_TOKEN);

        this.httpWrapper = new HttpWrapper();

        this.stringService = this.getAjsDependency_('stringService');
    }

    /**
     * Fetches a list of transport zones.
     */
    public async fetchTransportZones(): Promise<INsxtTransportzone[]> {
        const { nsxt_url: nsxtUrl, nsxt_credentials_ref: credentialsRef } = this.config;

        if (!this.hasUrlAndCredentials()) {
            return [];
        }

        const payload = {
            credentials_uuid: this.stringService.slug(credentialsRef),
            host: nsxtUrl,
        };

        this.busy = true;
        this.errors = null;

        try {
            const requestConfig = {
                url: TRANSPORT_ZONES_API,
                method: HttpMethod.POST,
                data: payload,
                requestId: 'transport-zones',
            };

            const response = await this.httpWrapper.request(requestConfig);
            const { resource = {} } = response.data;
            const { nsxt_transportzones: transportZones = [] } = resource;

            return transportZones;
        } catch (errors) {
            this.errors = errors.data;

            return Promise.reject(errors);
        } finally {
            this.busy = false;
        }
    }

    /**
     * Fetches NSX Tier1s. A VMC deployed cloud uses a cloud_uuid in the payload while a typical
     * NSX cloud will use the host URL and credentials_uuid.
     */
    public async fetchTier1s(cloudRef?: string): Promise<INsxtTier1[]> {
        const payload: Record<string, string> = {};

        if (cloudRef) {
            payload.cloud_uuid = this.stringService.slug(cloudRef);
        } else {
            if (!this.hasUrlAndCredentials()) {
                return [];
            }

            const {
                nsxt_url: nsxtUrl,
                nsxt_credentials_ref: credentialsRef,
            } = this.config;

            payload.credentials_uuid = this.stringService.slug(credentialsRef);
            payload.host = nsxtUrl;
        }

        this.busy = true;
        this.errors = null;

        try {
            const requestConfig = {
                url: TIER1S_API,
                method: HttpMethod.POST,
                data: payload,
                requestId: 'tier1s',
            };

            const response = await this.httpWrapper.request(requestConfig);
            const { resource = {} } = response.data;
            const { nsxt_tier1routers: tier1Routers = [] } = resource;

            return tier1Routers;
        } catch (errors) {
            this.errors = errors.data;

            return [];
        } finally {
            this.busy = false;
        }
    }

    /**
     * Fetch NSX segments for a VLAN transport zone.
     */
    public async fetchVlanSegments(
        transportZoneID: string,
    ): Promise<INsxtSegment[]> {
        const segments = await this.fetchSegments(transportZoneID);

        return segments;
    }

    /**
     * Fetches VCenter addresses.
     */
    public async fetchVCenters(): Promise<string[]> {
        if (!this.hasUrlAndCredentials()) {
            return [];
        }

        const {
            nsxt_url: nsxtUrl,
            nsxt_credentials_ref: credentialsRef,
            // transport zone used to fetch VCenters is given by data_network_config
            data_network_config: { transportZone },
        } = this.config;

        const payload = {
            credentials_uuid: this.stringService.slug(credentialsRef),
            host: nsxtUrl,
            transport_zone_id: transportZone,
        };

        this.busy = true;
        this.errors = null;

        try {
            const requestConfig = {
                url: VCENTERS_API,
                method: HttpMethod.POST,
                data: payload,
                requestId: 'vcenters',
            };

            const response = await this.httpWrapper.request(requestConfig);
            const { resource = {} } = response.data;
            const { vcenter_ips: vcenterIps = [] } = resource;

            return vcenterIps.map((vcenterIp: INsxtVcenterIp) => vcenterIp.vcenter_ip?.addr);
        } catch (errors) {
            this.errors = errors.data;

            return [];
        } finally {
            this.busy = false;
        }
    }

    /**
     * Returns true if both the NSX URL and Credentials are set.
     */
    public hasUrlAndCredentials(): boolean {
        const { nsxt_url: nsxtUrl, nsxt_credentials_ref: credentialsRef } = this.config;

        return !isUndefined(credentialsRef) && !isUndefined(nsxtUrl);
    }

    /**
     * Clear transport zone related data configured for child fields.
     */
    public clearTransportZoneData(): void {
        this.config.management_network_config.clearTransportZoneData();
        this.config.data_network_config.clearTransportZoneData();
    }

    /**
     * Called to verify NSX login credentials.
     */
    public async verifyNsxtLogin(loginCredentials: TNsxtLoginCredentials): Promise<void> {
        const stringService: StringService = this.getAjsDependency_('stringService');
        const {
            nsxt_url: nsxtUrl,
            nsxt_credentials_ref: credentialsRef,
        } = loginCredentials;

        const payload = {
            host: nsxtUrl,
            credentials_uuid: stringService.slug(credentialsRef),
        };

        this.busy = true;
        this.errors = null;

        try {
            const requestConfig = {
                url: VERIFY_LOGIN_API,
                method: HttpMethod.POST,
                data: payload,
                requestId: VERIFY_LOGIN_REQUEST_ID,
            };

            await this.httpWrapper.request(requestConfig);
        } catch (errors) {
            this.errors = errors.data;

            return Promise.reject(errors.data.error);
        } finally {
            this.busy = false;
        }
    }

    /**
     * Cancels any outstanding verify-login requests.
     */
    public cancelVerifyNsxtLogin(): void {
        this.httpWrapper.cancelRequest(VERIFY_LOGIN_REQUEST_ID);
    }

    /**
     * Sets the nsxt_url and nsxt_credentials_ref properties on the config.
     */
    public setNsxtLoginCredentials(loginCredentials: TNsxtLoginCredentials): void {
        const config = this.getConfig();

        const {
            nsxt_url: nsxtUrl,
            nsxt_credentials_ref: credentialsRef,
        } = loginCredentials;

        config.nsxt_url = nsxtUrl;
        config.nsxt_credentials_ref = credentialsRef;
    }

    /**
     * Returns true if the data_network_config is configured with an Overlay tz_type.
     */
    public isOverlayTransportZone(): boolean {
        return this.isTransportZoneType(NsxtTransportZoneType.OVERLAY);
    }

    /**
     * Returns true if the data_network_config is configured with a VLAN tz_type.
     */
    public isVlanTransportZone(): boolean {
        return this.isTransportZoneType(NsxtTransportZoneType.VLAN);
    }

    /**
     * Return true if the vpc_mode is enabled.
     */
    public isVpcEnabled(): boolean {
        return Boolean(this.config.vpc_mode);
    }

    /**
     * Called to destroy and cancel all pending requests.
     * @override
     */
    public destroy(): void {
        this.httpWrapper.cancelAllRequests();

        super.destroy();
    }

    /**
     * Call to cancel ongoing HTTP requests.
     */
    public cancelAllRequests(): void {
        this.httpWrapper.cancelAllRequests();
    }

    /**
     * Fetches tier1 segments by cloudRef and tier1LRID. Currently used by the
     * VmcDeploymentNsxtCloudModal only, since fetching segments previously required a transport
     * zone ID, NSX host URL, and NSX credentials, but with VMC deployment those fields are not
     * available.
     */
    public fetchSegmentsByCloudRef(
        cloudRef: string,
        tier1LRID: string,
    ): Promise<INsxtSegment[]> {
        const payload: ISegmentsRequestPayload = {
            cloud_uuid: this.stringService.slug(cloudRef),
            tier1_id: tier1LRID,
        };

        return this.fetchSegmentsByPayload(payload);
    }

    /**
     * Fetches NSX segments by credentials_uuid, host, and transport_zone_id.
     */
    public fetchSegments(transportZoneID: string): Promise<INsxtSegment[]> {
        if (!this.hasUrlAndCredentials()) {
            return Promise.resolve([]);
        }

        const {
            nsxt_url: nsxtUrl,
            nsxt_credentials_ref: credentialsRef,
        } = this.config;

        const payload: ISegmentsRequestPayload = {
            credentials_uuid: this.stringService.slug(credentialsRef),
            host: nsxtUrl,
            transport_zone_id: transportZoneID,
        };

        return this.fetchSegmentsByPayload(payload);
    }

    /** @override */
    protected requiredFields(): string[] {
        return [
            'management_network_config',
            'data_network_config',
        ];
    }

    /**
     * Returns true if the data_network_config is configured with a specified tz_type.
     */
    private isTransportZoneType(transportZoneType: NsxtTransportZoneType): boolean {
        return this.config.data_network_config.isTransportZoneType(transportZoneType);
    }

    /**
     * Fetches segments for a Tier1 given a payload. This method was added to avoid duplication
     * because a normal NSX configuration uses a different payload from a VMC deployed NSX
     * cloud.
     */
    private async fetchSegmentsByPayload(
        payload: ISegmentsRequestPayload,
    ): Promise<INsxtSegment[]> {
        this.busy = true;
        this.errors = null;

        const { tier1_id: tier1Id } = payload;

        try {
            const requestConfig = {
                url: SEGMENTS_API,
                method: HttpMethod.POST,
                data: payload,
                requestId: tier1Id,
            };

            const response = await this.httpWrapper.request(requestConfig);
            const { resource = {} } = response.data;
            const { nsxt_segments: segments = [] } = resource;

            return segments;
        } catch (errors) {
            this.errors = errors.data;

            return [];
        } finally {
            this.busy = false;
        }
    }
}

NsxtConfigurationConfigItem.ajsDependencies = [
    HTTP_WRAPPER_TOKEN,
    'stringService',
];
